// Zone Morte — client : planque, réseau, prédiction, caméra isométrique, tir, fouille, effets et interface.
import * as THREE from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { EXTRACT_TIME, GUN_HEIGHT, INFECTED, INTERACT_RANGE, INTERP_DELAY, SPRINT_TO_FIRE, TICK_DT } from '../shared/constants';
import { lineOfSight, raycast, rayCircle } from '../shared/geometry';
import { MAX_PITCH, bodyTop, bulletRay, muzzleHeight } from '../shared/ballistics';
import type { Loadout, Place } from '../shared/protocol';
import { ITEMS } from '../shared/items';
import { MAP, type GameMap } from '../shared/map';
import { condName } from '../shared/atmos';
import { Atmosphere } from './atmosphere';
import { F, helmetOf, packOf, vestOf, type BodyTuple, type ClientMsg, type ContView, type GameEvent, type InfectedTuple, type PlayerTuple, type ServerMsg, type Snapshot } from '../shared/protocol';
import { isSprinting, stepMove, type MoveInput } from '../shared/sim';
import { WEAPONS, WEAPON_ORDER, pelletOffsets, recoilOffset, shotInterval, weaponStats, type WeaponId } from '../shared/weapons';
import { Actor, infectedModel, loadActors, survivorModel } from './actors';
import { PALETTES } from './lowbody';
import { tracked } from './tracked';
import { countOf } from '../shared/inventory';
import { Audio3D } from './audio';
import { Soundscape } from './soundscape';
import { renderGrid, renderSlot, setDropHandler, setLoadoutSource } from './grid';
import { initIcons } from './icons';
import { QUESTS } from '../shared/quests';
import type { QuestState } from '../shared/quests';
import { closeSettings, initSettingsPanel, onSettings, openSettings, setAccount, settings, settingsOpen } from './settings';
import { Hideout } from './hideout';
import { Hud, hotbarMeds } from './hud';
import { buildWorld, loadProps, type World } from './world';
import { Grass } from './grass';
import { wind } from './lowpoly';

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
const store = {
  get: (k: string) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* indisponible */ } },
  del: (k: string) => { try { localStorage.removeItem(k); } catch { /* indisponible */ } },
};

// ---------------------------------------------------------------- Rendu

const renderer = new THREE.WebGLRenderer({ antialias: true, stencil: true, powerPreference: 'high-performance' });
renderer.setPixelRatio(Math.min(devicePixelRatio, 1.5));
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.12;
$('view').append(renderer.domElement);
const scene = new THREE.Scene();
scene.background = new THREE.Color(0x231f1a);
// Éclairage d'ambiance pour les matériaux réalistes (reflets doux sur le métal, le verre, la peinture)
scene.environment = new THREE.PMREMGenerator(renderer).fromScene(new RoomEnvironment(), 0.04).texture;
scene.environmentIntensity = 0.2;

// Caméra à la 3e personne, derrière l'épaule : yaw (cap) et pitch (inclinaison, + vers le haut) suivent la souris
let yaw = Math.PI / 4, pitch = -0.12;
const camera = new THREE.PerspectiveCamera(75, 1, 0.05, 700);
let onResize: (() => void) | null = null; // ambiance : son rendu (halo) suit la taille de la fenêtre
function resize() {
  renderer.setSize(innerWidth, innerHeight);
  camera.aspect = innerWidth / innerHeight;
  camera.updateProjectionMatrix();
  onResize?.();
}
addEventListener('resize', resize);
resize();

// Fin de journée : soleil bas et chaud, ciel bleuté qui débouche les ombres
const hemi = new THREE.HemisphereLight(0xa9bccb, 0x3b3226, 1.25);
scene.add(hemi);
const sun = new THREE.DirectionalLight(0xffcf96, 2.7);
const SUN = new THREE.Vector3(-70, 70, -45); // le soleil suit la caméra : ombres nettes sur toute la carte
sun.position.set(MAP.width / 2, 0, MAP.height / 2).add(SUN);
sun.target.position.set(MAP.width / 2, 0, MAP.height / 2);
sun.castShadow = true;
sun.shadow.mapSize.set(4096, 4096);
Object.assign(sun.shadow.camera, { left: -60, right: 60, top: 60, bottom: -60, near: 1, far: 260 });
sun.shadow.bias = -0.0005;
sun.shadow.normalBias = 0.03;
scene.add(sun, sun.target);
// Ambiance : heure et météo du raid (la planque garde la fin de journée)
const atmo = new Atmosphere(renderer, scene, camera, hemi, sun, SUN);
onResize = () => atmo.resize();
const muzzle = new THREE.PointLight(0xffd59a, 0, 10, 2);
scene.add(muzzle);

let world: World | null = null;
let grass: Grass | null = null; // herbe haute autour du joueur (raid seulement)
let drops: [number, number, number][] = [], deadMarks: [number, number, number][] = []; // restes d'infectés, corps récents des pillards
// Carte du raid en cours : comme MAP, plus les portes fermées à clé déjà ouvertes (collisions, vue, prédiction)
const LIVE: GameMap = { ...MAP, open: new Set<string>() };
const audio = new Audio3D();
const sound = new Soundscape(audio);
// Le navigateur n'autorise le son qu'après un geste : au premier clic ou à la première touche, on démarre l'ambiance
const screenMode = () => (!$('hud').classList.contains('hidden') ? 'raid' : !$('hideout').classList.contains('hidden') || !$('endScreen').classList.contains('hidden') ? 'hideout' : 'menu');
const wakeAudio = () => { void audio.init().then(() => { audio.resume(); sound.setMode(screenMode()); }); };
addEventListener('pointerdown', wakeAudio, { once: true });
addEventListener('keydown', wakeAudio, { once: true });
// Sons d'interface : clic, survol, onglets, achat, déploiement ; refus quand le bouton est désactivé
document.addEventListener('click', e => {
  const el = e.target as HTMLElement, b = el.closest('button') as HTMLButtonElement | null;
  if (!b) return;
  if (b.disabled) sound.ui('error');
  else if (b.id === 'raidGo') sound.ui('deploy');
  else if (b.closest('[data-buy]') || b.dataset.buy) sound.ui('buy');
  else if (b.closest('#hoTabs, #menuTabs, #qTabs, #charSub, #traderList, #qPartners')) sound.ui('tab');
  else sound.ui('click');
}, true);
document.addEventListener('pointerover', e => { if ((e.target as HTMLElement).closest?.('button:not(:disabled), .qRow, .it, .mk')) sound.ui('hover'); });
const hud = new Hud();

// ---------------------------------------------------------------- État

let ws: WebSocket | null = null;
let myId = 0, ping = 0, playing = false;
const roster = new Map<number, { name: string; bot: boolean }>();
interface Snap { tick: number; players: Map<number, PlayerTuple>; zs: Map<number, InfectedTuple>; bodies: BodyTuple[] }
const snaps: Snap[] = [];
let clockOffset: number | null = null; // temps serveur (s) - temps local (s)
let you: Snapshot['you'] | null = null;
let loadout: Loadout | null = null, slot: 1 | 2 = 1;
let cont: ContView | null = null, invOpen = false;
const searched = new Set<number>(); // meubles entièrement fouillés (leur repère est grisé)

// angle : visée (souris) ; body : orientation du corps (la visée, sauf en sprint où l'on fait face à sa course)
const lp = { x: 0, y: 0, z: 0, vz: 0, jcd: 0, angle: 0, pitch: 0, body: 0, alive: false, crouch: false, moving: false, sprint: false, ox: 0, oy: 0 };
const pending: MoveInput[] = [];
let outbox: MoveInput[] = [];
let seq = 0, lastFlush = 0, lastSprintAt = -9, jumpQueued = false;
const local = { weapon: null as WeaponId | null, mag: 0, nextShot: 0, lastShot: -9, shotIndex: 0, lastFireSent: -9 };
const actors = new Map<number, Actor>();
const zActors = new Map<number, Actor>();
const bodyActors = new Map<number, Actor>();
const keys = new Set<string>();
const mouse = { x: innerWidth / 2, y: innerHeight / 2, down: false, right: false, clicked: false };

const send = (m: ClientMsg) => { if (ws && ws.readyState === 1) ws.send(JSON.stringify(m)); };
const nowS = () => performance.now() / 1000;
const serverTime = () => nowS() + (clockOffset ?? 0);
const hideout = new Hideout(send, m => hud.toast(m));
// Infobulles : comparaison avec ce qui est porté (équipement de la planque, ou du raid en cours)
setLoadoutSource(() => (document.getElementById('hideout')!.classList.contains('hidden') ? loadout : hideout.loadout));

// ---------------------------------------------------------------- Réseau

function connect(name: string) {
  ws = new WebSocket(`${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}`);
  ws.onopen = () => send({ t: 'hello', name, token: store.get('zm-token') ?? undefined });
  ws.onmessage = e => onMessage(JSON.parse(e.data) as ServerMsg);
  ws.onclose = () => { playing = false; releasePointer(); hud.disconnected(); };
}

function onMessage(m: ServerMsg) {
  switch (m.t) {
    case 'profile':
      // Profil relié à Discord : pas de jeton d'invité (la session Discord suffit), on oublie l'ancien
      if (m.token) store.set('zm-token', m.token); else store.del('zm-token');
      quests = m.profile.quests;
      $('loading').classList.add('hidden');
      if (!playing && $('endScreen').classList.contains('hidden')) { hideout.show(m.profile, m.prices, m.unlock, m.now, m.dev); sound.setMode('hideout'); }
      else pendingProfile = m;
      break;
    case 'joined':
      myId = m.id;
      playing = true;
      resetRaid();
      atmo.set(m.cond ?? null);
      sound.setWeather(m.cond?.weather === 'rain', m.cond?.time === 'night');
      for (const id of m.open ?? []) LIVE.open!.add(id);
      world?.setDoors(LIVE.open!);
      hideout.hide();
      hud.showGame(m.cond ? `${m.room} · ${condName(m.cond)}` : m.room);
      prewarm();
      // Une case de saisie de la planque ne doit pas garder le clavier : sinon les touches du jeu seraient ignorées
      (document.activeElement as HTMLElement | null)?.blur();
      // Glisser-déposer en raid : prendre dans le conteneur, y déposer, ou ranger son sac
      setDropHandler((it, from, to, x, y) => {
        if (from === 'cont' && to !== 'cont' && to !== 'sell' && cont) send({ t: 'take', cont: cont.id, uid: it.uid, to, x, y });
        else if (to === 'cont' && from !== 'cont' && cont) send({ t: 'put', cont: cont.id, uid: it.uid, x, y });
        else if (from !== 'cont' && to !== 'cont' && to !== 'sell') send({ t: 'move', from: from as Place, uid: it.uid, to: to as Place, x, y });
      });
      audio.ambience();
      sound.setMode('raid');
      break;
    case 'inv': loadout = m.loadout; slot = m.slot; renderRaidInv(); break;
    case 'cont':
      cont = m.cont;
      if (cont) { invOpen = true; if (!cont.searching) searched.add(cont.id); }
      renderRaidInv();
      break;
    case 'end': atmo.set(null); sound.setWeather(false, false); onEnd(m); break;
    case 'roster':
      roster.clear();
      for (const p of m.players) roster.set(p.id, p);
      break;
    case 'pong': ping = Math.round(performance.now() - m.at); break;
    case 'social': hideout.social.set(m.social); break;
    case 'friendSearch': hideout.social.searchResults(m.results); break;
    case 'toast':
      hud.toast(m.msg);
      // Son selon le message : vente, fabrication, réussite, refus
      sound.ui(/^Vendu/.test(m.msg) ? 'sell' : /^Fabrication lancée/.test(m.msg) ? 'craft' : /^(Récupéré|Contrat rempli|Contrat accepté)|amélioré/.test(m.msg) ? 'notify' : /pas assez|manque|ne va pas|plein|requis|impossible|déjà|refus|Pas de place|Disponible au niveau|Il faut/i.test(m.msg) ? 'error' : 'click');
      break;
    case 'error': hud.toast(m.msg); break;
    case 's': if (playing) onSnapshot(m); break;
  }
}
let quests: QuestState = { active: {}, done: [] };
let shake = 0; // secousse de caméra (tir, impact)
let pendingProfile: Extract<ServerMsg, { t: 'profile' }> | null = null;

// Préchauffage (une fois) : un exemplaire de chaque personnage, armé et équipé, rendu sous le sol puis retiré.
// La carte graphique prépare ainsi ses programmes de rendu dès l'arrivée, au lieu de figer le jeu à la première rencontre.
let warmed = false;
function prewarm() {
  if (warmed || !world) return;
  warmed = true;
  const list = [
    new Actor(scene, survivorModel(1), PALETTES.bandits[0]), new Actor(scene, survivorModel(2)),
    new Actor(scene, infectedModel(0, 0), PALETTES.infected[0]), new Actor(scene, infectedModel(2, 1), PALETTES.brute), new Actor(scene, infectedModel(4, 2), PALETTES.crawler),
  ];
  const weapons: (WeaponId | null)[] = ['ar', 'pistol', null, null, null];
  list.forEach((a, i) => a.update(1 / 60, { x: lp.x, z: lp.y, y: -40, angle: 0, moving: false, sprint: false, crouch: false, dead: false, weapon: weapons[i] ?? null, helmet: i === 0 ? 'h_mil' : i === 1 ? 'h_hard' : null, vest: i === 0 ? 'v_mil' : i === 1 ? 'v_police' : null, pack: i === 0 ? 'b_raid' : i === 1 ? 'b_small' : null }));
  renderer.compile(scene, camera);
  setTimeout(() => { for (const a of list) a.dispose(); }, 1200);
}

function resetRaid() {
  LIVE.open!.clear();
  world?.setDoors(LIVE.open!);
  for (const a of [...actors.values(), ...zActors.values(), ...bodyActors.values()]) a.dispose();
  actors.clear(); zActors.clear(); bodyActors.clear();
  snaps.length = 0;
  pending.length = 0;
  clockOffset = null;
  lp.alive = false;
  cont = null;
  invOpen = false;
  renderRaidInv();
  camSnap = true;
}

function onSnapshot(s: Snapshot) {
  const t = nowS();
  const sample = s.tick * TICK_DT - t;
  // L'horloge suit le paquet le plus « frais » et dérive doucement vers les autres
  clockOffset = clockOffset === null || sample > clockOffset ? sample : clockOffset + (sample - clockOffset) * 0.02;
  drops = s.drops ?? [];
  deadMarks = s.dead ?? [];
  snaps.push({ tick: s.tick, players: new Map(s.p.map(p => [p[0], p])), zs: new Map(s.z.map(z => [z[0], z])), bodies: s.b });
  while (snaps.length > 40) snaps.shift();

  you = s.you;
  if (!lp.alive) { lp.x = s.you.x; lp.y = s.you.y; lp.z = s.you.z ?? 0; lp.vz = s.you.vz ?? 0; lp.alive = true; camSnap = true; }
  else {
    // Réconciliation : on repart de la position du serveur et on rejoue les entrées pas encore confirmées
    const px = lp.x, py = lp.y;
    while (pending.length && pending[0]!.seq <= s.ack) pending.shift();
    const st = { x: s.you.x, y: s.you.y, z: s.you.z ?? 0, vz: s.you.vz ?? 0, jcd: s.you.jcd ?? 0 };
    for (const i of pending) stepMove(LIVE, st, i);
    if (Math.hypot(st.x - px, st.y - py) < 1.5) { lp.ox += px - st.x; lp.oy += py - st.y; }
    lp.x = st.x; lp.y = st.y; lp.z = st.z; lp.vz = st.vz; lp.jcd = st.jcd;
  }
  if (s.you.w !== local.weapon) { local.weapon = s.you.w; local.shotIndex = 0; }
  if (nowS() - local.lastFireSent > 0.4) local.mag = s.you.mag;
  for (const ev of s.ev) onEvent(ev);
  hud.vitals(s.you, local.mag, ping, loadout, slot);
  // Bip à chaque seconde d'extraction, battements de cœur quand la santé est basse
  const extLeft = s.you.ext > 0 ? Math.ceil((1 - s.you.ext) * EXTRACT_TIME) : -1;
  if (extLeft >= 0 && extLeft !== lastExtLeft) sound.extractBeep(extLeft);
  lastExtLeft = extLeft;
  sound.heartbeat(s.you.hp);
  // Contrats d'élimination en cours : avancement avec les éliminations de ce raid
  const kinds = ['infected', 'bandit', 'boss'] as const;
  // Contrats suivis (bouton « Suivre ») ; sans contrat suivi, ceux d'élimination en cours. Livraisons : ce qu'on porte sur soi
  const track = tracked();
  const shown = QUESTS.filter(q => q.id in quests.active && (track.size ? track.has(q.id) : q.goal.kind !== 'deliver'));
  hud.quests(shown.map(q => {
    const g = q.goal;
    if (g.kind === 'deliver') return { title: q.title, have: Math.min(g.n, loadout ? countOf(loadout.bag, g.item) + countOf(loadout.pouch, g.item) : 0), n: g.n };
    return { title: q.title, have: Math.min(g.n, (quests.active[q.id] ?? 0) + s.you.tally[kinds.indexOf(g.kind)]!), n: g.n };
  }));
}

let lastExtLeft = -1;
function onEnd(m: Extract<ServerMsg, { t: 'end' }>) {
  playing = false;
  releasePointer(); // écran de fin : la souris doit revenir
  $('relock').classList.add('hidden');
  sound.setMode('hideout');
  sound.sting(m.result === 'extracted' ? 'extract' : 'death');
  if (m.levelUp) setTimeout(() => sound.sting('level'), 1400);
  lp.alive = false;
  invOpen = false;
  cont = null;
  renderRaidInv();
  // Serveur d'une version précédente : ces deux valeurs manquent, on les reconstitue avec ce que le client sait déjà
  m.totalXp ??= hideout.xp + m.xp;
  m.infected ??= you?.tally[0] ?? 0;
  hud.endScreen(m, () => {
    resetRaid();
    if (pendingProfile) { hideout.show(pendingProfile.profile, pendingProfile.prices, pendingProfile.unlock, pendingProfile.now, pendingProfile.dev); pendingProfile = null; }
  });
}

// ---------------------------------------------------------------- Événements

const tracers: { line: THREE.Line; until: number }[] = [];
const tracerMat = new THREE.LineBasicMaterial({ color: 0xffe2a0, transparent: true, opacity: 0.9 });
const puffs: { m: THREE.Mesh; until: number; v: number }[] = [];
const puffGeo = new THREE.SphereGeometry(0.12, 6, 4);
const puffMat = new THREE.MeshBasicMaterial({ color: 0xcfc6b5, transparent: true, opacity: 0.8 });
const bloodMat = new THREE.MeshBasicMaterial({ color: 0x7a1010, transparent: true, opacity: 0.9 });

function tracer(x0: number, y0: number, x1: number, y1: number, h0 = GUN_HEIGHT, h1 = h0) {
  const g = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(x0, h0, y0), new THREE.Vector3(x1, h1, y1)]);
  const line = new THREE.Line(g, tracerMat);
  scene.add(line);
  tracers.push({ line, until: performance.now() + 70 });
}
function puff(x: number, y: number, blood: boolean, h = GUN_HEIGHT) {
  for (let i = 0; i < (blood ? 5 : 3); i++) {
    const m = new THREE.Mesh(puffGeo, blood ? bloodMat : puffMat);
    m.position.set(x + (Math.random() - 0.5) * 0.3, h + (Math.random() - 0.5) * 0.3, y + (Math.random() - 0.5) * 0.3);
    scene.add(m);
    puffs.push({ m, until: performance.now() + 260 + Math.random() * 120, v: 0.6 + Math.random() });
  }
}
function flash(x: number, y: number) { muzzle.position.set(x, GUN_HEIGHT + 0.2, y); muzzle.intensity = 30; muzzle.userData.until = performance.now() + 55; }

// Une source est-elle à l'écran (un joueur ou un infecté visible à côté) ?
function sourceVisible(x: number, y: number) {
  const last = snaps[snaps.length - 1];
  if (!last) return false;
  for (const p of last.players.values()) if (p[0] !== myId && Math.hypot(p[1] - x, p[2] - y) < 1.5) return true;
  for (const z of last.zs.values()) if (Math.hypot(z[1] - x, z[2] - y) < 1.5) return true;
  return false;
}

function onEvent(ev: GameEvent) {
  switch (ev.k) {
    case 'shot': {
      if (ev.id !== myId) {
        for (const e of ev.ends) tracer(ev.x, ev.y, e[0], e[1], muzzleHeight(false), e[3] ?? GUN_HEIGHT);
        flash(ev.x, ev.y);
        actors.get(ev.id)?.fired();
        const d = Math.hypot(ev.x - lp.x, ev.y - lp.y);
        sound.shot(ev.w, ev.x, ev.y, d, !!ev.sup);
        if (!sourceVisible(ev.x, ev.y)) hud.soundCue(angleTo(ev.x, ev.y), 'shot');
      }
      let sounded = false;
      for (const [x, y, hit, h] of ev.ends) {
        puff(x, y, hit === 1, Math.max(0.05, h ?? GUN_HEIGHT));
        // Le corps touché réagit
        if (hit === 1) for (const a of [...actors.values(), ...zActors.values()]) if (a.root.visible && Math.hypot(a.root.position.x - x, a.root.position.z - y) < 0.8) a.hit();
        if (hit !== 1 && !sounded && Math.hypot(x - lp.x, y - lp.y) < 30) { sounded = true; audio.play(audio.pick(hit === 2 ? 'wood' : 'metal', 2), { x, z: y, vol: 0.5 }); }
      }
      break;
    }
    case 'step':
      audio.play(audio.pick('step', 5), { x: ev.x, z: ev.y, vol: ev.run ? 0.9 : 0.55, ref: 3, max: 25 });
      if (!sourceVisible(ev.x, ev.y)) hud.soundCue(angleTo(ev.x, ev.y), 'step');
      break;
    case 'reload': audio.play(`reload_${ev.w}`, { x: ev.x, z: ev.y, vol: 0.7, ref: 2, max: 12 }); break;
    case 'hitmark':
      hud.hitmark(!!ev.kill, !!ev.armor);
      // Balle arrêtée par une protection : bruit métallique
      audio.play(ev.armor && !ev.kill ? audio.pick('metal', 2) : ev.kill ? 'hit_1' : 'hit_0', { vol: ev.kill ? 0.9 : 0.5, rate: ev.kill ? 0.8 : 1.4 });
      break;
    case 'hurt':
      hud.hurt(angleTo(ev.fromX, ev.fromY));
      shake = Math.min(1, shake + 0.6);
      actors.get(myId)?.hit();
      audio.play(audio.pick('body', 2), { vol: 1 });
      break;
    case 'kill': {
      const k = ev.killer ? roster.get(ev.killer)?.name ?? '?' : 'Infecté', v = roster.get(ev.victim)?.name ?? '?';
      hud.killfeed(k, v, ev.killer ? WEAPONS[ev.w].short : 'morsure', ev.killer === myId, ev.victim === myId);
      break;
    }
    case 'groan':
      audio.play(audio.pick(ev.loud ? 'growl' : 'zombie', ev.loud ? 4 : 6), { x: ev.x, z: ev.y, vol: ev.loud ? 1.3 : 0.8, ref: 3, max: 30 });
      if (!sourceVisible(ev.x, ev.y)) hud.soundCue(angleTo(ev.x, ev.y), 'step');
      break;
    case 'swipe': audio.play(audio.pick('growl', 4), { x: ev.x, z: ev.y, vol: 1.1, rate: 1.3, ref: 3, max: 20 }); break;
    case 'search': audio.play(audio.pick('rummage', 2), { x: ev.x, z: ev.y, vol: 0.7, ref: 2, max: 10 }); break;
    case 'door': LIVE.open!.add(ev.lock); world?.setDoors(LIVE.open!); sound.ui('craft'); break;
    case 'extracted': if (ev.id !== myId) hud.toast(`${roster.get(ev.id)?.name ?? 'Quelqu’un'} s’est extrait.`); break;
  }
}

// Angle à l'écran (radians, 0 = vers le haut) vers un point du monde, pour les indicateurs autour du joueur
const tmpV = new THREE.Vector3(), tmpW = new THREE.Vector3();
// Direction d'un point par rapport au regard (0 : devant, + : à droite)
function angleTo(x: number, y: number) {
  return Math.atan2(y - lp.y, x - lp.x) - Math.atan2(-Math.cos(yaw), -Math.sin(yaw));
}
// Point du monde à l'écran ; derrière la caméra, il est renvoyé loin hors de l'écran (du bon côté)
function screenOf(x: number, y: number, h = 1) {
  tmpV.set(x, h, y).applyMatrix4(camera.matrixWorldInverse);
  const behind = tmpV.z > -0.05;
  tmpV.set(x, h, y).project(camera);
  if (behind) { tmpV.x = -tmpV.x * 1e4 || 1e4; tmpV.y = -tmpV.y * 1e4; }
  return { x: (tmpV.x * 0.5 + 0.5) * innerWidth, y: (-tmpV.y * 0.5 + 0.5) * innerHeight };
}
void tmpW;

// ---------------------------------------------------------------- Inventaire en raid

// Clic sur le butin : le prendre. Clic sur son sac (conteneur ouvert) : le déposer. Clic droit : utiliser ou équiper.
function renderRaidInv() {
  $('raidInv').classList.toggle('hidden', !invOpen || !playing);
  $('contPanel').classList.toggle('hidden', !invOpen || !cont || !playing);
  if (!invOpen || !loadout) return;
  const mine = {
    click: (it: { uid: number }) => { if (cont) send({ t: 'put', cont: cont.id, uid: it.uid }); },
    context: (it: { uid: number; id: string }) => {
      const d = ITEMS[it.id]!;
      if (d.cat === 'med') send({ t: 'use', uid: it.uid });
      else if (d.cat === 'weapon' || d.cat === 'armor') send({ t: 'equip', uid: it.uid });
    },
  };
  renderSlot($('rSlot1'), loadout.primary, 'primary', '1 · Principale', { click: mine.click }, slot === 1);
  renderSlot($('rSlot2'), loadout.secondary, 'secondary', '2 · Pistolet', { click: mine.click }, slot === 2);
  renderSlot($('rSlot3'), loadout.helmet, 'helmet', 'Casque', { click: mine.click });
  renderSlot($('rSlot4'), loadout.vest, 'vest', 'Gilet', { click: mine.click });
  renderSlot($('rSlot5'), loadout.backpack, 'backpack', 'Sac à dos', { click: mine.click });
  $('rBagTitle').textContent = loadout.backpack ? ITEMS[loadout.backpack.id]!.name : 'Poches';
  renderGrid($('rBag'), loadout.bag, 'bag', mine);
  renderGrid($('rPouch'), loadout.pouch, 'pouch', mine);
  if (cont) {
    $('contName').textContent = cont.name;
    $('contState').textContent = cont.searching ? `Fouille en cours… encore ${cont.hidden} objet${cont.hidden > 1 ? 's' : ''}` : cont.items.items.length ? 'Fouillé' : 'Vide';
    renderGrid($('contGrid'), cont.items, 'cont', { click: it => send({ t: 'take', cont: cont!.id, uid: it.uid }) }, { slots: cont.slots, next: cont.next });
  }
}

// Repères des meubles à fouiller proches et visibles (celui qui est à portée est mis en avant)
const CONT_SHORT: Record<string, string> = { restes: 'Restes', coffre: 'Coffre-fort', militaire: 'Caisse militaire', outils: 'Outils', caisse: 'Caisse', armoire: 'Armoire', bureau: 'Bureau', casier: 'Casier d’armes', medical: 'Pharmacie', corps: 'Corps' };
function contMarks(px: number, py: number, near: { id: number } | null) {
  const out: { key: string; label: string; pos: { x: number; y: number }; state: 'near' | 'far' | 'done' | 'dead' }[] = [];
  const add = (id: number, kind: string, x: number, y: number) => {
    if (Math.hypot(x - px, y - py) > 9 || !lineOfSight(LIVE, px, py, x, y)) return;
    out.push({ key: String(id), label: CONT_SHORT[kind] ?? kind, pos: screenOf(x, y, 1.6), state: near && near.id === id ? 'near' : searched.has(id) ? 'done' : 'far' });
  };
  for (const c of MAP.containers) add(c.id, c.kind, c.x, c.y);
  for (const b of snaps[snaps.length - 1]?.bodies ?? []) add(b[5], 'corps', b[1], b[2]);
  for (const d of drops) add(d[0], 'restes', d[1], d[2]);
  // Corps de pillards tués depuis moins de 2 minutes : repère visible de loin, avec la distance
  for (const [x, y] of deadMarks) {
    const d = Math.hypot(x - px, y - py);
    if (d > 9) out.push({ key: `dead${x},${y}`, label: `☠ Corps · ${Math.round(d)} m`, pos: screenOf(x, y, 1.2), state: 'dead' });
  }
  return out;
}

// Meuble ou corps le plus proche, à portée de main
function nearestCont(): { id: number; name: string; x: number; y: number } | null {
  let best: { id: number; name: string; x: number; y: number } | null = null, bd = INTERACT_RANGE;
  for (const c of MAP.containers) {
    const d = Math.hypot(c.x - lp.x, c.y - lp.y);
    if (d < bd) { bd = d; best = { id: c.id, name: c.kind, x: c.x, y: c.y }; }
  }
  const last = snaps[snaps.length - 1];
  for (const b of last?.bodies ?? []) {
    const d = Math.hypot(b[1] - lp.x, b[2] - lp.y);
    if (d < bd) { bd = d; best = { id: b[5], name: 'corps', x: b[1], y: b[2] }; }
  }
  for (const r of drops) {
    const d = Math.hypot(r[1] - lp.x, r[2] - lp.y);
    if (d < bd) { bd = d; best = { id: r[0], name: 'restes', x: r[1], y: r[2] }; }
  }
  return best;
}
// Porte fermée à clé la plus proche, à portée de main
function nearestLock() {
  let best: { lock: (typeof MAP.locks)[number]; d: number } | null = null;
  for (const l of MAP.locks) {
    if (LIVE.open!.has(l.id)) continue;
    const d = Math.hypot(l.x - lp.x, l.y - lp.y);
    if (d < INTERACT_RANGE + 0.8 && (!best || d < best.d)) best = { lock: l, d };
  }
  return best;
}
const lockPrompt = (l: (typeof MAP.locks)[number]) => {
  const key = loadout && [...loadout.bag.items, ...loadout.pouch.items].find(i => ITEMS[i.id]?.key?.lock === l.id);
  const def = ITEMS[l.key]!;
  return key ? `[E] Ouvrir : ${l.name} (${def.short} · ${key.uses ?? def.key!.uses} utilisation${(key.uses ?? def.key!.uses) > 1 ? 's' : ''})` : `${l.name} : verrouillée · il faut la ${def.name.charAt(0).toLowerCase()}${def.name.slice(1)}`;
};
const CONT_LABEL: Record<string, string> = { restes: 'les restes', coffre: 'le coffre-fort', militaire: 'la caisse militaire', outils: 'le coffre à outils', caisse: 'la caisse', armoire: 'l’armoire', bureau: 'le bureau', casier: 'le casier d’armes', medical: 'l’armoire à pharmacie', corps: 'le corps' };

// ---------------------------------------------------------------- Contrôles

addEventListener('keydown', e => {
  if (!playing || (e.target as HTMLElement).tagName === 'INPUT') return;
  keys.add(e.code);
  if (e.code === 'Tab') { e.preventDefault(); if (!e.repeat) { invOpen = !invOpen; if (!invOpen && cont) { send({ t: 'close' }); cont = null; } renderRaidInv(); syncPointer(); } return; }
  if (e.repeat) return;
  // Échap : ferme ce qui est ouvert (réglages, inventaire), sinon ouvre les réglages
  if (e.code === 'Escape') {
    if (settingsOpen()) { closeSettings(); syncPointer(); }
    else if (invOpen || cont) { invOpen = false; if (cont) { send({ t: 'close' }); cont = null; } renderRaidInv(); syncPointer(); }
    else openSettings(true);
  }
  if (e.code === 'KeyR') send({ t: 'reload' });
  if (e.code === 'KeyC') lp.crouch = !lp.crouch;
  if (e.code === 'KeyE') {
    // Porte fermée à clé à portée : on l'ouvre (le serveur vérifie la clé)
    const lk = nearestLock();
    if (lk && (!nearestCont() || lk.d < Math.hypot(nearestCont()!.x - lp.x, nearestCont()!.y - lp.y))) { send({ t: 'unlock', lock: lk.lock.id }); return; }
    const c = nearestCont();
    if (c && cont && cont.id === c.id) { send({ t: 'close' }); cont = null; invOpen = false; renderRaidInv(); syncPointer(); }
    else if (c) send({ t: 'open', id: c.id });
  }
  if (e.code === 'KeyV' && loadout) {
    // Soin : d'abord arrêter le saignement, puis l'attelle, puis le soin le mieux ajusté à la blessure
    const meds = [...loadout.bag.items, ...loadout.pouch.items].filter(i => ITEMS[i.id]!.cat === 'med');
    const missing = 100 - (you?.hp ?? 100), st = you?.status ?? 0;
    const urgent = st & 1 ? meds.find(i => i.id === 'm_bandage') ?? meds.find(i => ITEMS[i.id]!.stopBleed) : st & 2 ? meds.find(i => ITEMS[i.id]!.splint) : undefined;
    const pick = urgent ?? meds.filter(i => ITEMS[i.id]!.heal).sort((a, b) => Math.abs((ITEMS[a.id]!.heal ?? 0) - missing) - Math.abs((ITEMS[b.id]!.heal ?? 0) - missing))[0];
    if (pick) send({ t: 'use', uid: pick.uid }); else hud.toast('Aucun soin dans le sac.');
  }
  // Touches 3 à 5 : utiliser le soin de la case correspondante de la barre d'objets
  if ((e.code === 'Digit3' || e.code === 'Digit4' || e.code === 'Digit5') && loadout) {
    const m = hotbarMeds(loadout)[Number(e.code.slice(5)) - 3];
    const it = m && [...loadout.bag.items, ...loadout.pouch.items].find(i => i.id === m.id);
    if (it) send({ t: 'use', uid: it.uid });
  }
  if (e.code === 'Digit1' || e.code === 'Digit2') {
    const s = e.code === 'Digit1' ? 1 : 2;
    if (s !== slot && loadout && (s === 1 ? loadout.primary : loadout.secondary)) { send({ t: 'slot', s }); local.nextShot = nowS() + 0.35; local.shotIndex = 0; audio.play(audio.pick('cloth', 2), { vol: 0.6 }); }
  }
  if (e.code === 'KeyQ') shoulder = -shoulder; // A en AZERTY : changer d'épaule
  if (e.code === 'Space') { e.preventDefault(); jumpQueued = true; }
});
addEventListener('keyup', e => keys.delete(e.code));
addEventListener('blur', () => { keys.clear(); mouse.down = mouse.right = false; });
const cv = renderer.domElement;
cv.addEventListener('contextmenu', e => e.preventDefault());
// Souris capturée en raid : elle oriente la caméra. Libérée quand l'inventaire, un conteneur ou les réglages sont ouverts.
const locked = () => document.pointerLockElement === cv;
const wantsPointer = () => playing && lp.alive && !invOpen && !cont && !settingsOpen();
// Reprise de la souris : seulement sur un geste du joueur (clic, touche), le navigateur refuse sinon.
// selfRelease : c'est le jeu qui l'a libérée (inventaire, mort…), ce n'est pas Échap.
let selfRelease = false;
function syncPointer() {
  if (wantsPointer() && !locked()) void cv.requestPointerLock()?.catch?.(() => { /* il faudra cliquer */ });
  else if (!wantsPointer() && locked()) { selfRelease = true; document.exitPointerLock(); }
}
// Le jeu rend la souris (fin de raid, déconnexion)
function releasePointer() { if (locked()) { selfRelease = true; document.exitPointerLock(); } }
document.addEventListener('pointerlockchange', () => {
  if (locked()) { selfRelease = false; return; }
  mouse.down = mouse.right = false;
  if (selfRelease) { selfRelease = false; return; }
  // Échap libère la souris (le navigateur ne nous transmet pas la touche) : on ouvre les réglages, comme une pause
  if (wantsPointer()) openSettings(true);
});
$('setClose').addEventListener('click', () => setTimeout(syncPointer));
addEventListener('mousemove', e => {
  mouse.x = e.clientX; mouse.y = e.clientY;
  if (!locked()) return;
  // Certains navigateurs envoient parfois un saut énorme en souris capturée : on l'ignore
  if (Math.abs(e.movementX) > 500 || Math.abs(e.movementY) > 500) return;
  const k = 0.0022 * Math.pow(2, (settings.sens - 50) / 30) * (scopeW > 0.5 ? 0.3 : aimW > 0.5 ? 0.65 : 1);
  yaw -= e.movementX * k;
  pitch = clamp(pitch - e.movementY * k, -MAX_PITCH, 1.0);
  // Lunette : le tube suit la main avec un temps de retard, l'image bouge dans le cercle
  if (scopeW > 0.5) { sway.vx -= e.movementX * 9; sway.vy -= e.movementY * 9; }
});
cv.addEventListener('mousedown', e => {
  audio.resume();
  if (!locked()) { if (wantsPointer()) syncPointer(); return; } // premier clic : reprendre la souris, sans tirer
  if (e.button === 0) { mouse.down = true; mouse.clicked = true; }
  if (e.button === 2) mouse.right = true;
});
addEventListener('mouseup', e => { if (e.button === 0) mouse.down = false; if (e.button === 2) mouse.right = false; });

// ---------------------------------------------------------------- Boucle

const ray = new THREE.Raycaster();
const aimPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -GUN_HEIGHT);
const ndc = new THREE.Vector2();
const aimPoint = new THREE.Vector3();
let lastFrame = performance.now(), stepAcc = 0, camX = MAP.width / 2, camZ = MAP.height / 2, pingAt = 0, camSnap = true;

let fpsCount = 0, fpsAt = 0;
function frame(nowMs: number) {
  requestAnimationFrame(frame);
  const dt = Math.min(0.05, (nowMs - lastFrame) / 1000);
  lastFrame = nowMs;
  fpsCount++;
  wind.value += dt;
  if (nowMs - fpsAt > 500) { $('fps').textContent = `${Math.round((fpsCount * 1000) / (nowMs - fpsAt))} i/s`; fpsCount = 0; fpsAt = nowMs; }
  if (!playing || !world) {
    // Planque : la caméra survole lentement la ville
    camX = MAP.width / 2 + Math.cos(nowMs / 30000) * 45; camZ = MAP.height / 2 + Math.sin(nowMs / 30000) * 30;
    const o = nowMs / 40000;
    camera.fov = 50; camera.updateProjectionMatrix();
    camera.position.set(camX + Math.sin(o) * 70, 48, camZ + Math.cos(o) * 70);
    camera.lookAt(camX, 0, camZ);
    followSun(camX, camZ);
    grass?.update(camX, camZ, false);
    world?.cut.value.set(-999, 0, -999);
    world?.updateRoofs(-999, -999);
    atmo.frame(dt, { x: camX, z: camZ }, null, []);
    atmo.render();
    return;
  }
  if (lp.alive) updateLocal(dt);
  if (nowMs - lastFlush > 33 && outbox.length) { send({ t: 'in', inputs: outbox }); outbox = []; lastFlush = nowMs; }
  if (nowMs - pingAt > 2000) { pingAt = nowMs; send({ t: 'ping', at: performance.now() }); }
  lp.ox *= Math.pow(0.001, dt); lp.oy *= Math.pow(0.001, dt);
  const px = lp.x + lp.ox, py = lp.y + lp.oy;

  // Caméra : derrière l'épaule ; au clic droit elle se rapproche, avec une lunette on passe à la 1re personne
  if (!wantsPointer() && locked()) syncPointer(); // fouille, inventaire : on rend la souris
  // Souris libre : flèche visible et réticule caché ; sans rien d'ouvert, on invite à cliquer pour reprendre la visée
  document.body.classList.toggle('mouse-free', !locked());
  $('relock').classList.toggle('hidden', locked() || !wantsPointer());
  const [vx, vy] = camLook ?? [px, py]; // débogage : caméra posée sur un autre point
  if (camSnap) camSnap = false;
  camX = vx; camZ = vy;
  placeCamera(dt, vx, vy);
  audio.listen(px, py, yaw);
  world.cut.value.set(-999, 0, -999);
  world.updateRoofs(-999, -999);
  grass?.update(px, py, settings.quality !== 'basse');

  updateActors(dt, px, py);
  // À la lunette, l'œil est dans la tête : son propre corps et son arme ne doivent pas passer devant
  const me = actors.get(myId);
  if (me && scopeW > 0.5) me.root.visible = false;
  updateFx(nowMs, dt);
  // Ambiance : voile autour du joueur, pluie, réverbères, lampes torches (la sienne, puis celles des plus proches)
  const others = [...actors].filter(([id, a]) => id !== myId && a.root.visible && !a.isDead)
    .map(([, a]) => ({ x: a.root.position.x, z: a.root.position.z, angle: Math.PI / 2 - a.root.rotation.y }))
    .sort((a, b) => Math.hypot(a.x - px, a.z - py) - Math.hypot(b.x - px, b.z - py)).slice(0, 4);
  const sp = screenOf(px, py, 1);
  atmo.frame(dt, { x: px, z: py }, { x: px, z: py, sx: sp.x, sy: sp.y }, lp.alive ? [{ x: px, z: py, angle: lp.angle }, ...others] : others);
  atmo.render();
  const near = nearestCont(), lockNear = lp.alive ? nearestLock() : null;
  hud.compass((-yaw * 180) / Math.PI);
  hud.frame(nowMs, {
    mouseX: innerWidth / 2, mouseY: innerHeight / 2, aiming: mouse.right, alive: lp.alive, onTarget: !!hovered, nextImpact: nextImpactScreen(px, py), player: { x: innerWidth / 2, y: innerHeight / 2 + 40 },
    stance: lp.crouch ? 'Accroupi' : mouse.right ? 'Épaulé' : lp.sprint ? 'Sprint' : 'Debout',
    prompt: lockNear && (!near || lockNear.d < Math.hypot(near.x - px, near.y - py)) ? lockPrompt(lockNear.lock) : near && !(cont && cont.id === near.id) ? `[E] Fouiller ${CONT_LABEL[near.name] ?? near.name}` : '',
    extracts: MAP.extracts.map(e => ({ name: e.cost ? `${e.name} · ${e.cost.toLocaleString('fr-FR')} cr` : e.name, dist: Math.hypot(e.x - px, e.y - py), pos: screenOf(e.x, e.y, 2.5) })),
    conts: contMarks(px, py, near),
  });
}
requestAnimationFrame(frame);

let shoulder = 1, shoulderS = 1, aimW = 0, scopeW = 0, recoilDebt = 0, camDist = 3.1, camSide = 0.55;
// Lunette : décalage du tube (px) et sa vitesse, ressort amorti
const sway = { x: 0, y: 0, vx: 0, vy: 0 }, scopeOff = { x: 0, y: 0 };
const camFwd = new THREE.Vector3(), camRight = new THREE.Vector3(), camPos = new THREE.Vector3();
function placeCamera(dt: number, px: number, py: number) {
  const w = heldStats();
  const aiming = mouse.right && lp.alive && locked();
  const scoped = aiming && (w?.reach ?? 1) >= 1.6; // lunette (intégrée ou × 4)
  aimW += ((aiming ? 1 : 0) - aimW) * Math.min(1, dt * 12);
  scopeW += ((scoped ? 1 : 0) - scopeW) * Math.min(1, dt * 14);
  // Recul : la visée monte à chaque tir, puis redescend en partie toute seule
  const back = recoilDebt * Math.min(1, dt * 5);
  pitch = clamp(pitch - back, -MAX_PITCH, 1.0); recoilDebt -= back;
  // Respiration à la lunette : la visée ondule doucement (moins accroupi, plus en marchant)
  const t = performance.now() / 1000, br = scopeW * (lp.crouch ? 0.5 : 1) * (lp.moving ? 2.2 : 1);
  // (sway : en pixels d'écran, convertis en angle ; la visée suit la main avec un temps de retard)
  const pr = THREE.MathUtils.degToRad(camera.fov) / innerHeight;
  const vYaw = yaw - sway.x * pr + Math.sin(t * 0.83) * 0.0016 * br, vPitch = pitch - sway.y * pr + Math.sin(t * 1.27 + 1) * 0.0012 * br;
  camFwd.set(-Math.sin(vYaw) * Math.cos(vPitch), Math.sin(vPitch), -Math.cos(vYaw) * Math.cos(vPitch));
  camRight.set(Math.cos(yaw), 0, -Math.sin(yaw));
  const pivotH = (lp.crouch ? 1.15 : 1.62) + lp.z;
  // Changement d'épaule : la caméra glisse d'un côté à l'autre
  shoulderS += (shoulder - shoulderS) * Math.min(1, dt * 9);
  if (scopeW > 0.5) {
    // Lunette : l'œil derrière l'arme
    camPos.set(px, pivotH - 0.08, py).addScaledVector(camFwd, 0.15);
  } else {
    // Épaule, poussée contre les murs ; la caméra recule moins quand on vise.
    // Un mur rapproche la caméra tout de suite (jamais à travers), elle ne s'éloigne qu'en douceur (pas de saut).
    const want = (0.55 - 0.1 * aimW) * Math.abs(shoulderS), sgn = shoulderS < 0 ? -1 : 1;
    const sideAng = Math.atan2(camRight.z * sgn, camRight.x * sgn);
    const sideMax = Math.max(0, Math.min(want, raycast(LIVE, px, py, sideAng, want + 0.3, false).t - 0.3));
    camSide = sideMax < camSide ? sideMax : camSide + (sideMax - camSide) * Math.min(1, dt * 6);
    const sx = px + camRight.x * camSide * sgn, sz = py + camRight.z * camSide * sgn;
    let dist = 3.1 - 1.6 * aimW;
    if (pitch > 0.05) dist = Math.min(dist, (pivotH - 0.3) / Math.sin(pitch)); // pas sous le sol en regardant en l'air
    const hx = -camFwd.x, hz = -camFwd.z, hl = Math.hypot(hx, hz);
    if (hl > 1e-3) {
      const free = raycast(LIVE, sx, sz, Math.atan2(hz, hx), dist * hl + 0.3, false).t - 0.3;
      dist = Math.max(0.2, Math.min(dist, free / hl));
    }
    camDist = dist < camDist ? dist : camDist + (dist - camDist) * Math.min(1, dt * 5);
    camPos.set(sx, pivotH + 0.12, sz).addScaledVector(camFwd, -camDist);
  }
  shake *= 0.85;
  camera.position.set(camPos.x + (Math.random() - 0.5) * shake * 0.04, camPos.y + (Math.random() - 0.5) * shake * 0.04, camPos.z);
  camera.lookAt(camera.position.x + camFwd.x, camera.position.y + camFwd.y, camera.position.z + camFwd.z);
  const zoom = scopeW > 0.5 ? (w?.reach ?? 1.6) * 2.1 : 1 + aimW * (0.2 + 0.5 * ((w?.reach ?? 1) - 1));
  const fov = THREE.MathUtils.radToDeg(2 * Math.atan(Math.tan(THREE.MathUtils.degToRad(settings.fov / 2)) / zoom));
  if (Math.abs(camera.fov - fov) > 0.01) { camera.fov = fov; camera.updateProjectionMatrix(); }
  camera.updateMatrixWorld();
  // Inertie de l'arme à la lunette : l'image glisse dans le tube puis se recale (ressort), le tube reste fixe
  sway.vx += (-sway.x * 90 - sway.vx * 13) * dt; sway.vy += (-sway.y * 90 - sway.vy * 13) * dt;
  sway.x = clamp(sway.x + sway.vx * dt, -90, 90); sway.y = clamp(sway.y + sway.vy * dt, -90, 90);
  $('scope').classList.toggle('hidden', scopeW <= 0.5);
  if (scopeW <= 0.5) sway.x = sway.y = sway.vx = sway.vy = 0;
  followSun(px, py);
}
// Ombres : la zone couverte suit le joueur, par pas d'un texel pour éviter le scintillement
function followSun(x: number, z: number) {
  const step = 120 / sun.shadow.mapSize.x, gx = Math.round(x / step) * step, gz = Math.round(z / step) * step;
  sun.target.position.set(gx, 0, gz);
  sun.position.set(gx + SUN.x, SUN.y, gz + SUN.z);
}

// Ce que vise le réticule (centre de l'écran) : mur, sol, joueur ou infecté. On part du joueur, pas de la caméra
// (rien de ce qui est entre la caméra et lui ne compte).
const aimDir = new THREE.Vector3();
function resolveAim() {
  // À la lunette, on vise par le réticule (décalé par le balancement du tube), sinon par le centre de l'écran
  const o = camera.position;
  const f = scopeW > 0.5 ? aimDir.set((2 * scopeOff.x) / innerWidth, (-2 * scopeOff.y) / innerHeight, 0.5).unproject(camera).sub(o).normalize() : camFwd;
  const hl = Math.hypot(f.x, f.z) || 1e-3, ang = Math.atan2(f.z, f.x), slope = f.y / hl, dx = Math.cos(ang), dy = Math.sin(ang);
  const t0 = Math.max(0, (lp.x - o.x) * dx + (lp.y - o.z) * dy);
  const ox = o.x + dx * t0, oy = o.z + dy * t0, oh = o.y + slope * t0;
  const wall = bulletRay(LIVE, ox, oy, ang, 400, Math.max(0.01, oh), slope);
  let T = wall.t;
  hovered = null;
  const consider = (a: Actor, top: number) => {
    if (!a.root.visible || a.isDead) return;
    const p = a.root.position, t = rayCircle(ox, oy, dx, dy, p.x, p.z, 0.45, T), y = oh + slope * t - p.y; // p.y : en saut, sur un objet
    if (t < T && y >= 0 && y <= top + 0.05) { T = t; hovered = { x: p.x, z: p.z }; }
  };
  for (const [id, a] of actors) if (id !== myId) consider(a, bodyTop(a.crouching));
  for (const a of zActors.values()) consider(a, a.top);
  aimPoint.set(ox + dx * T, oh + slope * T, oy + dy * T);
}

function updateLocal(dt: number) {
  // Déplacement relatif à la caméra (ZQSD / WASD : on lit la position physique des touches)
  let fx = 0, fz = 0;
  const fwd = [-Math.sin(yaw), -Math.cos(yaw)] as const, right = [Math.cos(yaw), -Math.sin(yaw)] as const;
  if (keys.has('KeyW') || keys.has('ArrowUp')) { fx += fwd[0]; fz += fwd[1]; }
  if (keys.has('KeyS') || keys.has('ArrowDown')) { fx -= fwd[0]; fz -= fwd[1]; }
  if (keys.has('KeyD') || keys.has('ArrowRight')) { fx += right[0]; fz += right[1]; }
  if (keys.has('KeyA') || keys.has('ArrowLeft')) { fx -= right[0]; fz -= right[1]; }
  const l = Math.hypot(fx, fz);
  // Visée : l'arme pointe vers ce que montre le réticule (à bout portant, droit devant soi)
  resolveAim();
  const ad = Math.hypot(aimPoint.x - lp.x, aimPoint.z - lp.y);
  lp.angle = ad > 0.9 ? Math.atan2(aimPoint.z - lp.y, aimPoint.x - lp.x) : Math.atan2(camFwd.z, camFwd.x);
  lp.pitch = ad > 0.9 ? Math.atan2(aimPoint.y - muzzleHeight(lp.crouch) - lp.z, ad) : pitch;
  const input: MoveInput = {
    seq: ++seq, dt, mx: l ? fx / l : 0, my: l ? fz / l : 0,
    sprint: (keys.has('ShiftLeft') || keys.has('ShiftRight')) && !mouse.down, crouch: lp.crouch, aim: mouse.right, angle: Math.round(lp.angle * 1000) / 1000,
    limp: !!you && (you.status & 2) > 0,
    pitch: Math.round(lp.pitch * 100) / 100,
    ...(jumpQueued ? { jump: true } : {}),
  };
  jumpQueued = false;
  lp.moving = l > 0;
  lp.sprint = isSprinting(input);
  // En sprint, on court face à sa direction (pas de course à reculons) ; le serveur reçoit cette orientation
  if (lp.sprint) input.angle = Math.round(Math.atan2(input.my, input.mx) * 1000) / 1000;
  lp.body = input.angle;
  if (lp.sprint) lastSprintAt = nowS();
  const ox = lp.x, oy = lp.y, wasAir = lp.vz !== 0;
  stepMove(LIVE, lp, input);
  // Réception d'un saut : bruit de pas un peu plus fort
  if (wasAir && lp.vz === 0) audio.play(audio.pick('step', 5), { vol: 0.55 });
  pending.push(input);
  outbox.push(input);
  if (pending.length > 240) pending.shift();
  stepAcc += Math.hypot(lp.x - ox, lp.y - oy);
  if (stepAcc > (lp.sprint ? 1.7 : lp.crouch ? 1.0 : 1.3)) { stepAcc = 0; audio.play(audio.pick('step', 5), { vol: lp.crouch ? 0.2 : lp.sprint ? 0.6 : 0.4 }); }
  tryFire();
}

let hovered: { x: number; z: number } | null = null; // joueur ou infecté sous le réticule

// Arme en main avec ses accessoires
const heldItem = () => (slot === 1 ? loadout?.primary : loadout?.secondary) ?? null;
const heldStats = () => (local.weapon ? weaponStats(local.weapon, heldItem()?.mods) : null);

function tryFire() {
  if (!local.weapon) return;
  const w = heldStats()!;
  const t = nowS();
  // On ne tire que souris capturée (pas à travers l'inventaire ou les réglages)
  if (!mouse.down || !locked()) { mouse.clicked = false; return; }
  if (!w.auto && !mouse.clicked) return;
  if ((you?.reload ?? 0) > 0 || t < local.nextShot || t - lastSprintAt < SPRINT_TO_FIRE) return;
  if (local.mag <= 0) {
    if (mouse.clicked) { audio.play('dry', { vol: 0.6 }); send({ t: 'reload' }); }
    mouse.clicked = false;
    return;
  }
  mouse.clicked = false;
  local.shotIndex = t - local.lastShot > w.recover ? 0 : local.shotIndex + 1;
  local.lastShot = t;
  local.nextShot = Math.max(local.nextShot, t - 0.02) + shotInterval(w);
  local.mag--;
  local.lastFireSent = t;
  send({ t: 'fire', angle: lp.angle, view: Math.floor((serverTime() - INTERP_DELAY) / TICK_DT), pitch: Math.round(lp.pitch * 1000) / 1000 });
  // Prédiction : traçantes et son tout de suite, les impacts exacts viendront du serveur
  const base = lp.angle + (recoilOffset(w, local.shotIndex, mouse.right) * Math.PI) / 180;
  const h0 = muzzleHeight(lp.crouch) + lp.z, slope = Math.tan(lp.pitch);
  for (const off of pelletOffsets(w)) {
    const a = base + (off * Math.PI) / 180;
    const sx = lp.x + Math.cos(a) * 0.45, sy = lp.y + Math.sin(a) * 0.45;
    const hit = bulletRay(LIVE, sx, sy, a, w.range * 2, h0, slope);
    tracer(sx, sy, sx + Math.cos(a) * hit.t, sy + Math.sin(a) * hit.t, h0, hit.y);
  }
  // Recul vertical : la visée monte (on le compense en tirant la souris vers le bas)
  const kick = (w.id === 'sg' || w.id === 'bolt' || w.id === 'dmr' ? 0.045 : w.auto ? 0.011 : 0.022) * w.recoilMul * (mouse.right ? 0.75 : 1) * (lp.crouch ? 0.8 : 1);
  pitch = clamp(pitch + kick, -MAX_PITCH, 1.0); recoilDebt += kick * 0.55;
  yaw += (Math.random() - 0.5) * kick * 0.4;
  flash(lp.x + Math.cos(base) * 0.5, lp.y + Math.sin(base) * 0.5);
  sound.shot(w.id, undefined, undefined, 0, w.suppressed);
  if (w.id === 'sg') setTimeout(() => audio.play('rack', { vol: 0.7 }), 420);
  actors.get(myId)?.fired();
  hud.kick(w.auto ? 0.6 : 1);
  shake = Math.min(1, shake + (w.id === 'sg' || w.id === 'dmr' ? 0.35 : 0.15));
}

// Où partira la prochaine balle si l'on continue la rafale (le motif de recul est fixe : on peut le compenser)
function nextImpactScreen(px: number, py: number): { x: number; y: number } | null {
  if (!lp.alive || !local.weapon) return null;
  const w = heldStats()!, t = nowS();
  if (t - local.lastShot > w.recover || w.pellets) return null;
  const off = (recoilOffset(w, local.shotIndex + 1, mouse.right) * Math.PI) / 180;
  const d = Math.hypot(aimPoint.x - px, aimPoint.z - py);
  const a = lp.angle + off;
  return screenOf(px + Math.cos(a) * d, py + Math.sin(a) * d, aimPoint.y);
}

// Joueurs et infectés : interpolés entre deux instantanés, avec INTERP_DELAY de retard
const lerpAngle = (a: number, b: number, f: number) => a + ((((b - a) % (Math.PI * 2)) + Math.PI * 3) % (Math.PI * 2) - Math.PI) * f;
function updateActors(dt: number, px: number, py: number) {
  const rt = serverTime() - INTERP_DELAY;
  let a = snaps[0], b = snaps[0];
  for (let i = snaps.length - 1; i >= 0; i--) if (snaps[i]!.tick * TICK_DT <= rt) { a = snaps[i]; b = snaps[i + 1] ?? snaps[i]; break; }
  if (!a || !b) return;
  const span = (b.tick - a.tick) * TICK_DT, f = span > 0 ? clamp((rt - a.tick * TICK_DT) / span, 0, 1) : 0;

  for (const [id, pb] of b.players) {
    let act = actors.get(id);
    // Apparence : le joueur et les autres joueurs en soldat, les pillards en civils armés
    if (!act) { act = new Actor(scene, survivorModel(id === myId ? 0 : 1 + (id % 3)), pb[5] & F.BOT ? PALETTES.bandits[id % PALETTES.bandits.length] : PALETTES.soldier); actors.set(id, act); }
    act.root.visible = true;
    if (id === myId) {
      act.update(dt, { x: px, z: py, y: lp.z, air: lp.vz !== 0, vy: lp.vz, pitch: lp.pitch, angle: lp.body, moving: lp.moving, sprint: lp.sprint, crouch: lp.crouch, dead: false, weapon: local.weapon, search: !!cont && !lp.moving, aim: mouse.right, mods: heldItem()?.mods, helmet: loadout?.helmet?.id ?? null, vest: loadout?.vest?.id ?? null, pack: loadout?.backpack?.id ?? null, reload: (you?.reload ?? 0) > 0, heal: (you?.heal ?? 0) > 0 });
      continue;
    }
    const pa = a.players.get(id) ?? pb;
    act.update(dt, {
      x: pa[1] + (pb[1] - pa[1]) * f, z: pa[2] + (pb[2] - pa[2]) * f, angle: lerpAngle(pa[3], pb[3], f),
      y: (pa[7] ?? 0) + ((pb[7] ?? 0) - (pa[7] ?? 0)) * f, air: Math.abs((pb[7] ?? 0) - (pa[7] ?? 0)) > 0.02, vy: span > 0 ? ((pb[7] ?? 0) - (pa[7] ?? 0)) / span : 0, pitch: (pa[8] ?? 0) + ((pb[8] ?? 0) - (pa[8] ?? 0)) * f,
      moving: !!(pb[5] & F.MOVING), sprint: !!(pb[5] & F.SPRINT), crouch: !!(pb[5] & F.CROUCH), dead: false, weapon: WEAPON_ORDER[pb[4]] ?? null, search: !!(pb[5] & F.SEARCH) && !(pb[5] & F.MOVING), aim: !!(pb[5] & F.AIM), helmet: helmetOf(pb[5]), vest: vestOf(pb[5]), pack: packOf(pb[5]), reload: !!(pb[5] & F.RELOAD), heal: !!(pb[5] & F.HEAL),
    });
  }
  for (const [id, act] of actors) if (!b.players.has(id)) { act.dispose(); actors.delete(id); }

  for (const [id, zb] of b.zs) {
    let act = zActors.get(id);
    if (!act) {
      const look = zb[4] === 2 ? PALETTES.brute : zb[4] === 3 ? PALETTES.screamer : zb[4] === 4 ? PALETTES.crawler : PALETTES.infected[id % PALETTES.infected.length];
      act = new Actor(scene, infectedModel(zb[4], id), look);
      act.setKind(INFECTED[zb[4]]?.scale ?? 1, zb[4] === 4);
      zActors.set(id, act);
    }
    const za = a.zs.get(id) ?? zb;
    act.root.visible = true;
    act.update(dt, { x: za[1] + (zb[1] - za[1]) * f, z: za[2] + (zb[2] - za[2]) * f, angle: lerpAngle(za[3], zb[3], f), moving: zb[5] === 1 || zb[5] === 2, sprint: zb[5] === 2, crouch: false, dead: zb[5] === 4, weapon: null, attack: zb[5] === 3 });
  }
  // Hors de vue : caché (le serveur ne l'envoie plus), oublié au bout de 20 s
  for (const [id, act] of zActors) {
    if (b.zs.has(id)) { act.root.userData.seen = performance.now(); continue; }
    act.root.visible = false;
    if (performance.now() - (act.root.userData.seen ?? 0) > 20000) { act.dispose(); zActors.delete(id); }
  }
  for (const bd of b.bodies) {
    let act = bodyActors.get(bd[0]);
    if (!act) { act = new Actor(scene, survivorModel(bd[4]), bd[4] ? PALETTES.bandits[bd[4] % PALETTES.bandits.length] : PALETTES.soldier); bodyActors.set(bd[0], act); }
    act.update(dt, { x: bd[1], z: bd[2], angle: bd[3], moving: false, sprint: false, crouch: false, dead: true, weapon: null });
  }
  hud.nameTags([...b.players.values()].filter(p => p[0] !== myId).map(p => ({ id: p[0], name: roster.get(p[0])?.name ?? '?', hp: p[6], pos: screenOf(p[1], p[2]) })));
}

function updateFx(nowMs: number, dt: number) {
  for (let i = tracers.length - 1; i >= 0; i--) if (nowMs > tracers[i]!.until) { tracers[i]!.line.removeFromParent(); tracers[i]!.line.geometry.dispose(); tracers.splice(i, 1); }
  for (let i = puffs.length - 1; i >= 0; i--) {
    const p = puffs[i]!;
    p.m.position.y += dt * p.v * 0.6;
    p.m.scale.multiplyScalar(1 + dt * 2.5);
    if (nowMs > p.until) { p.m.removeFromParent(); puffs.splice(i, 1); }
  }
  if (muzzle.userData.until && nowMs > muzzle.userData.until) muzzle.intensity = 0;
}

// ---------------------------------------------------------------- Accueil

const nameInput = $<HTMLInputElement>('name');
nameInput.value = store.get('zm-name') ?? '';
const urlRoom = new URLSearchParams(location.search).get('r');
if (urlRoom) $<HTMLInputElement>('raidCode').value = urlRoom.toUpperCase();

// Compte : session Discord (cookie) ou invité. Avec Discord, on relie le profil invité de ce navigateur au compte.
interface Me { discord: boolean; only: boolean; user: { name: string; avatar: string | null } | null }
let me: Me = { discord: false, only: false, user: null };
async function loadAccount() {
  try { me = await (await fetch('auth/me', { cache: 'no-store' })).json() as Me; } catch { /* serveur ancien : invité */ }
  const u = me.user;
  setAccount({ discord: me.discord, user: u });
  $('account').classList.toggle('hidden', !u);
  $('guest').classList.toggle('hidden', !!u || me.only);
  // Bouton Discord : actif si le serveur est configuré, sinon grisé (« bientôt »), caché une fois connecté
  const dl = $<HTMLButtonElement>('discordLogin');
  dl.classList.toggle('hidden', !!u);
  dl.disabled = !me.discord;
  dl.querySelector('span')!.textContent = me.discord ? 'Se connecter avec Discord' : 'Connexion Discord — bientôt';
  $('play').classList.toggle('hidden', !u && me.only);
  if (u) $('account').innerHTML = `${u.avatar ? `<img src="${u.avatar}" alt="">` : ''}<span class="who2"><small>Connecté avec Discord</small><b>${u.name.replace(/[<>&]/g, '')}</b></span><a href="auth/logout">Se déconnecter</a>`;
  const note = $('loginNote'), err = new URLSearchParams(location.search).get('login');
  if (err) {
    note.textContent = err === 'refused' ? 'Connexion Discord annulée ou expirée, réessaie.' : 'La connexion Discord a échoué, réessaie dans un instant.';
    note.className = 'loginNote bad';
    history.replaceState(null, '', location.pathname);
  } else if (me.discord && !u) {
    note.textContent = me.only ? 'Ce serveur demande un compte Discord.' : 'Avec Discord, ta progression te suit sur tous tes appareils. Ton profil invité actuel y sera relié.';
    note.className = 'loginNote';
  }
}
$('discordLogin').onclick = () => {
  // Le profil invité de ce navigateur sera relié au compte (cookie de courte durée, lu une seule fois par le serveur)
  const t = store.get('zm-token');
  if (t) document.cookie = `zm_guest=${encodeURIComponent(t)}; path=/; max-age=600; SameSite=Lax`;
  location.href = 'auth/discord';
};
void loadAccount();

// Carte du raid : le vrai monde low-poly photographié du ciel (vue plongeante depuis le sud, nord en haut, fin de journée),
// sans brouillard de guerre, toits en place. k : pixels par mètre. at(x, y) : position d'un point du sol sur l'image (en %).
const MAP_PITCH = THREE.MathUtils.degToRad(58);
function renderMapImage(k = 7): { url: string; at: (x: number, y: number) => [number, number]; ratio: number } {
  if (!world) return { url: '', at: () => [0, 0], ratio: 1 };
  const halfH = (MAP.height * Math.sin(MAP_PITCH)) / 2 + 4;
  const W = Math.round(MAP.width * k), H = Math.round(halfH * 2 * k);
  const cam = new THREE.OrthographicCamera(-MAP.width / 2, MAP.width / 2, halfH, -halfH, 1, 600);
  const c = new THREE.Vector3(MAP.width / 2, 0, MAP.height / 2);
  cam.position.copy(c).add(new THREE.Vector3(0, Math.sin(MAP_PITCH), Math.cos(MAP_PITCH)).multiplyScalar(250));
  cam.lookAt(c);
  cam.updateMatrixWorld(true);
  const at = (x: number, y: number): [number, number] => { const p = new THREE.Vector3(x, 0, y).project(cam); return [(p.x + 1) * 50, (1 - p.y) * 50]; };
  // Toute la carte dans l'ombre portée, coupe des murs et brouillard hors de la carte
  const sc = sun.shadow.camera, saved = { l: sc.left, r: sc.right, t: sc.top, b: sc.bottom, far: sc.far };
  Object.assign(sc, { left: -190, right: 190, top: 190, bottom: -190, far: 420 });
  sc.updateProjectionMatrix();
  sun.target.position.set(MAP.width / 2, 0, MAP.height / 2);
  sun.position.set(MAP.width / 2 + SUN.x * 1.6, SUN.y * 1.6, MAP.height / 2 + SUN.z * 1.6);
  sun.shadow.map?.dispose(); sun.shadow.map = null;
  world.cut.value.set(-999, 0, -999);
  world.updateRoofs(-999, -999);
  const fogWas = scene.fog; scene.fog = null;
  const size = renderer.getSize(new THREE.Vector2()), ratio = renderer.getPixelRatio();
  const exposure = renderer.toneMappingExposure;
  renderer.toneMappingExposure = 0.92; // un peu plus sombre : fin de journée
  renderer.setPixelRatio(1);
  renderer.setSize(W, H, false);
  renderer.render(scene, cam);
  renderer.toneMappingExposure = exposure;
  const out = document.createElement('canvas');
  out.width = W; out.height = H;
  out.getContext('2d')!.drawImage(renderer.domElement, 0, 0);
  // Remise en état pour le jeu
  renderer.setPixelRatio(ratio);
  scene.fog = fogWas;
  renderer.setSize(size.x, size.y, false);
  Object.assign(sc, { left: saved.l, right: saved.r, top: saved.t, bottom: saved.b, far: saved.far });
  sc.updateProjectionMatrix();
  (sun.shadow.map as THREE.WebGLRenderTarget | null)?.dispose(); sun.shadow.map = null;
  return { url: out.toDataURL('image/jpeg', 0.88), at, ratio: W / H };
}

async function start() {
  const name = nameInput.value.trim(); // vide : le serveur garde le nom du profil (ou « Récupérateur » s'il est nouveau)
  if (name) store.set('zm-name', name);
  hud.loading(0, 'Chargement des personnages…');
  let a = 0, b = 0;
  const progress = () => hud.loading(a * 0.45 + b * 0.35, a < 1 ? 'Chargement des personnages…' : 'Chargement du décor…');
  await Promise.all([loadActors(f => { a = f; progress(); }), loadProps(MAP, f => { b = f; progress(); }), audio.init(), initIcons()]);
  // On laisse l'écran se rafraîchir avant les étapes qui bloquent le navigateur (délai de secours : onglet en arrière-plan)
  const paint = () => new Promise(res => { requestAnimationFrame(() => requestAnimationFrame(res)); setTimeout(res, 60); });
  hud.loading(0.85, 'Construction de la zone…');
  await paint();
  if (!world) { world = buildWorld(MAP, scene); atmo.attachWorld(world.root); const shot = renderMapImage(); hideout.setMapImage(shot.url, shot.at, shot.ratio); grass = new Grass(MAP, scene); }
  hud.loading(0.95, 'Initialisation de la planque…');
  await paint();
  connect(name);
}
$('play').onclick = () => void start();
$('menuSettings').onclick = () => openSettings(false);
$('navSettings').onclick = () => openSettings(false);
// Accueil : pages Actualités, Feuille de route, À propos ; « ? » ramène aux commandes
function menuPage(id: string) {
  document.querySelectorAll<HTMLButtonElement>('#menuTabs button').forEach(b => b.classList.toggle('on', b.dataset.page === id));
  document.querySelectorAll<HTMLElement>('.menuPage').forEach(el => el.classList.toggle('hidden', el.id !== `page-${id}`));
}
$('menuTabs').onclick = e => { const b = (e.target as HTMLElement).closest('button'); if (b?.dataset.page) menuPage(b.dataset.page); };
$('menuHelp').onclick = () => {
  menuPage('home');
  const c = $('menuControls');
  c.classList.remove('flash'); void c.offsetWidth; c.classList.add('flash');
};
$('hoSettings').onclick = () => openSettings(false);

// Réglages : son, qualité d'image, ombres, repères, compteur d'images
initSettingsPanel();
const SHADOW_SIZE = { basse: 1024, moyenne: 2048, haute: 4096 } as const;
const PIXEL_RATIO = { basse: 0.75, moyenne: 1, haute: Math.min(devicePixelRatio, 1.5) } as const;
let lastShadow = '';
onSettings(s => {
  audio.setVolumes({ master: s.master / 100, sfx: s.sfx / 100, amb: s.amb / 100, music: s.music / 100, ui: s.ui / 100 });
  renderer.setPixelRatio(PIXEL_RATIO[s.quality]);
  atmo.setQuality(s.quality);
  resize();
  const key = `${s.shadows}-${s.quality}`;
  if (key !== lastShadow) {
    lastShadow = key;
    renderer.shadowMap.enabled = s.shadows;
    sun.castShadow = s.shadows;
    sun.shadow.mapSize.set(SHADOW_SIZE[s.quality], SHADOW_SIZE[s.quality]);
    sun.shadow.map?.dispose();
    sun.shadow.map = null;
    // Les matériaux doivent être recompilés quand on active ou coupe les ombres
    scene.traverse(o => { const m = (o as THREE.Mesh).material; if (m) for (const mm of Array.isArray(m) ? m : [m]) mm.needsUpdate = true; });
  }
  $('contMarks').style.display = s.markers ? '' : 'none';
  $('fps').classList.toggle('hidden', !s.fps);
});
nameInput.addEventListener('keydown', e => { if (e.key === 'Enter') $('play').click(); });

let camLook: [number, number] | null = null;
declare global { interface Window { __zm: unknown } }
window.__zm = { lp, local, get you() { return you; }, get loadout() { return loadout; }, get cont() { return cont; }, send, scene, camera, keys, mouse, snaps, audio, sound, hideout, atmo, look: (x?: number, y?: number) => { camLook = x === undefined ? null : [x, y ?? 0]; }, view: (y: number, p: number) => { yaw = y; pitch = p; }, aim: () => ({ x: aimPoint.x, y: aimPoint.y, z: aimPoint.z, angle: lp.angle, pitch: lp.pitch, hovered }), msg: (m: ServerMsg) => onMessage(m) }; // msg : débogage, simuler un message du serveur
